/**
 * Read a stored time, whatever shape it arrived in.
 *
 * Firestore hands times back as Timestamps, not Dates, and
 * `new Date(timestamp)` / `new Date(String(timestamp))` are Invalid Date.
 * These take a Timestamp, a Date, an ISO string, epoch milliseconds, or a
 * Timestamp that went through JSON (`{ seconds }` / `{ _seconds }`), and give
 * null for anything else, including a pending `serverTimestamp()`.
 *
 * No Firebase import: shared/dues/status.ts uses this, and both are synced into
 * functions/ (scripts/sync-shared-to-functions.mjs).
 */

interface TimestampLike {
  toDate: () => Date
}

interface SerializedTimestamp {
  seconds?: unknown
  nanoseconds?: unknown
  _seconds?: unknown
  _nanoseconds?: unknown
}

function fromSerialized(value: SerializedTimestamp): Date | null {
  const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds
  if (typeof seconds !== 'number') return null
  const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : value._nanoseconds
  return new Date(seconds * 1000 + (typeof nanos === 'number' ? Math.floor(nanos / 1e6) : 0))
}

export function toDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null
  let date: Date | null = null
  if (value instanceof Date) {
    date = value
  }
  else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value)
  }
  else if (typeof value === 'object') {
    date = typeof (value as Partial<TimestampLike>).toDate === 'function'
      ? (value as TimestampLike).toDate()
      : fromSerialized(value as SerializedTimestamp)
  }
  return date && !Number.isNaN(date.getTime()) ? date : null
}

export function toMillis(value: unknown): number | null {
  return toDate(value)?.getTime() ?? null
}
